import { useCallback, useEffect, useMemo, useState } from 'react';
import JournalPrintBar from './JournalPrintBar';
import { usePanelToast } from '../../../context/PanelToastContext';
import {
  formatCurrency,
  mergeCoaTreeWithLiveData,
  localDateKey,
} from './financeData';
import { fetchCoaTree } from '../../../services/admin/financeLedgerApi';
import {
  printFinanceList,
  exportFinanceCsv,
  exportFinanceExcel,
  exportFinancePdf,
  exportFinance,
} from './exportFinance';

const emptyApplied = {
  parentCode: 'all',
  childCode: 'all',
  startDate: '',
  endDate: '',
};

function collectEntries(tree, { parentCode, childCode }) {
  const rows = [];
  (tree || []).forEach((parent) => {
    if (parentCode !== 'all' && parent.code !== parentCode) return;
    (parent.children || []).forEach((child) => {
      if (childCode !== 'all' && child.code !== childCode) return;
      (child.entries || []).forEach((entry) => {
        rows.push({
          ...entry,
          parentCode: parent.code,
          parentName: parent.name,
          childCode: child.code,
          childName: child.name,
        });
      });
    });
  });
  return rows.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

function inDateRange(date, startDate, endDate) {
  const d = String(date || '').slice(0, 10);
  if (!d || d === '—') return !startDate && !endDate;
  if (startDate && d < startDate) return false;
  if (endDate && d > endDate) return false;
  return true;
}

function journalEntryType(row) {
  const ref = String(row?.ref || row?.reference || '').trim().toUpperCase();
  if (/^JV\b/.test(ref) || ref.startsWith('JV-') || ref.startsWith('JV_') || /^JV\d/.test(ref)) {
    return 'Ledgers';
  }
  const type = String(row?.type || '').trim();
  if (type === 'Ledgers' || type.toLowerCase() === 'ledger') return 'Ledgers';
  if (type === 'Bank' || type === 'Cash') return type;
  if (/^BPV|^BRV/i.test(ref)) return 'Bank';
  if (/^CPV|^CRV/i.test(ref)) return 'Cash';
  const method = String(row?.paymentMethod || row?.method || '').toLowerCase();
  if (method === 'ledgers' || method === 'ledger' || method === 'journal') return 'Ledgers';
  if (method === 'bank') return 'Bank';
  if (method === 'cash') return 'Cash';
  if (method === 'card') return 'Card';
  if (method === 'qr') return 'QR';
  return '—';
}

/**
 * Journal Ledger: parent → child filters, start/end date, Search / Clear + print/export.
 */
function JournalLedger({ gymId, liveData }) {
  const toast = usePanelToast();
  const [baseTree, setBaseTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [draft, setDraft] = useState({ ...emptyApplied });
  const [applied, setApplied] = useState({ ...emptyApplied });
  const [hasSearched, setHasSearched] = useState(false);

  const tree = useMemo(
    () => mergeCoaTreeWithLiveData(baseTree, liveData || {}),
    [baseTree, liveData],
  );

  const loadTree = useCallback(async () => {
    if (!gymId) {
      setBaseTree([]);
      setLoading(false);
      return;
    }
    try {
      setError('');
      setLoading(true);
      const next = await fetchCoaTree(gymId);
      setBaseTree(next);
    } catch (err) {
      setError(err.message || 'Failed to load accounts');
      setBaseTree([]);
    } finally {
      setLoading(false);
    }
  }, [gymId]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const parentOptions = useMemo(
    () => tree.map((p) => ({ code: p.code, name: p.name })),
    [tree],
  );

  const childOptions = useMemo(() => {
    if (draft.parentCode === 'all') {
      return tree.flatMap((p) => p.children.map((c) => ({
        code: c.code,
        name: `${p.name} / ${c.name}`,
        parentCode: p.code,
      })));
    }
    const parent = tree.find((p) => p.code === draft.parentCode);
    return (parent?.children || []).map((c) => ({
      code: c.code,
      name: c.name,
      parentCode: parent.code,
    }));
  }, [tree, draft.parentCode]);

  const filteredEntries = useMemo(() => {
    if (!hasSearched) return [];
    const rows = collectEntries(tree, {
      parentCode: applied.parentCode,
      childCode: applied.childCode,
    });
    return rows.filter((row) => inDateRange(row.date, applied.startDate, applied.endDate));
  }, [tree, applied, hasSearched]);

  const totals = useMemo(() => ({
    debit: filteredEntries.reduce((s, e) => s + (Number(e.debit) || 0), 0),
    credit: filteredEntries.reduce((s, e) => s + (Number(e.credit) || 0), 0),
  }), [filteredEntries]);

  const handleParentChange = (value) => {
    setDraft((d) => ({
      ...d,
      parentCode: value,
      childCode: 'all',
    }));
  };

  const handleSearch = (e) => {
    e?.preventDefault?.();
    let endDate = draft.endDate;
    let startDate = draft.startDate;
    if (startDate && endDate && endDate < startDate) {
      endDate = startDate;
    }
    setApplied({
      parentCode: draft.parentCode || 'all',
      childCode: draft.childCode || 'all',
      startDate: startDate || '',
      endDate: endDate || '',
    });
    setHasSearched(true);
  };

  const handleClear = () => {
    setDraft({ ...emptyApplied });
    setApplied({ ...emptyApplied });
    setHasSearched(false);
  };

  const appliedLabel = useMemo(() => {
    const parts = [];
    if (applied.parentCode === 'all') parts.push('All parents');
    else {
      const p = tree.find((x) => x.code === applied.parentCode);
      parts.push(p?.name || applied.parentCode);
    }
    if (applied.childCode === 'all') parts.push('All children');
    else {
      const child = tree
        .flatMap((p) => p.children)
        .find((c) => c.code === applied.childCode);
      parts.push(child?.name || applied.childCode);
    }
    if (applied.startDate || applied.endDate) {
      parts.push(`${applied.startDate || '…'} → ${applied.endDate || '…'}`);
    }
    return parts.join(' · ');
  }, [applied, tree]);

  const runExport = useCallback((action) => {
    // If user hasn't searched yet, apply current draft filters first
    let rows = filteredEntries;
    let label = appliedLabel;
    let debitTotal = totals.debit;
    let creditTotal = totals.credit;

    if (!hasSearched) {
      let endDate = draft.endDate;
      let startDate = draft.startDate;
      if (startDate && endDate && endDate < startDate) endDate = startDate;
      const nextApplied = {
        parentCode: draft.parentCode || 'all',
        childCode: draft.childCode || 'all',
        startDate: startDate || '',
        endDate: endDate || '',
      };
      setApplied(nextApplied);
      setHasSearched(true);
      rows = collectEntries(tree, {
        parentCode: nextApplied.parentCode,
        childCode: nextApplied.childCode,
      }).filter((row) => inDateRange(row.date, nextApplied.startDate, nextApplied.endDate));
      debitTotal = rows.reduce((s, e) => s + (Number(e.debit) || 0), 0);
      creditTotal = rows.reduce((s, e) => s + (Number(e.credit) || 0), 0);
      const parts = [];
      if (nextApplied.parentCode === 'all') parts.push('All parents');
      else {
        const p = tree.find((x) => x.code === nextApplied.parentCode);
        parts.push(p?.name || nextApplied.parentCode);
      }
      if (nextApplied.childCode === 'all') parts.push('All children');
      else {
        const child = tree.flatMap((p) => p.children).find((c) => c.code === nextApplied.childCode);
        parts.push(child?.name || nextApplied.childCode);
      }
      if (nextApplied.startDate || nextApplied.endDate) {
        parts.push(`${nextApplied.startDate || '…'} → ${nextApplied.endDate || '…'}`);
      }
      label = parts.join(' · ');
    }

    if (!rows.length) {
      toast.showError('No ledger entries match these filters.', 'Nothing to export');
      return;
    }

    const report = {
      title: `Journal Ledger${label ? ` — ${label}` : ''}`,
      slug: 'finance-journal-ledger',
      headers: ['Date', 'Parent', 'Child', 'Description', 'Ref', 'Type', 'Debit', 'Credit'],
      rows: rows.map((row) => [
        row.date || '—',
        row.parentName || row.parentCode || '—',
        row.childName || row.childCode || '—',
        row.description || '—',
        row.ref || '—',
        journalEntryType(row),
        row.debit ? formatCurrency(row.debit) : '—',
        row.credit ? formatCurrency(row.credit) : '—',
      ]),
      summary: [
        `Filters: ${label || 'All'}`,
        `Entries: ${rows.length}`,
        `Total Debit: ${formatCurrency(debitTotal)}`,
        `Total Credit: ${formatCurrency(creditTotal)}`,
      ],
    };

    const runners = {
      print: printFinanceList,
      csv: exportFinanceCsv,
      pdf: exportFinancePdf,
      excel: exportFinanceExcel,
      export: exportFinance,
    };
    runners[action]?.(report);
    if (action === 'print') {
      toast.showSuccess('Print preview opened.', 'Print');
    } else {
      toast.showSuccess(`${String(action).toUpperCase()} download started.`, 'Exported');
    }
  }, [
    filteredEntries,
    appliedLabel,
    totals,
    hasSearched,
    draft,
    tree,
    toast,
  ]);

  useEffect(() => {
    const onExport = (event) => {
      const action = event?.detail?.action;
      if (action) runExport(action);
    };
    window.addEventListener('finance-journal-export', onExport);
    return () => window.removeEventListener('finance-journal-export', onExport);
  }, [runExport]);

  return (
    <>
      <JournalPrintBar onAction={runExport} />

      <div className="owner-card rec-panel-card admin-finance-section admin-finance-journal-ledger">
        <div className="admin-finance-member-header" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 12, paddingRight: 24 }}>
          <div>
            <h2 className="owner-card-title">Journal Ledger</h2>
            <p className="owner-placeholder-text" style={{ margin: '0 0 0 20px' }}>
              {hasSearched
                ? `${filteredEntries.length} entries · Debit ${formatCurrency(totals.debit)} · Credit ${formatCurrency(totals.credit)}`
                : 'Set filters and click Search to view the ledger'}
            </p>
          </div>
        </div>

        <form className="admin-finance-journal-filters" onSubmit={handleSearch}>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="journal-parent">Parent account</label>
          <select
            id="journal-parent"
            className="owner-form-select"
            value={draft.parentCode}
            onChange={(e) => handleParentChange(e.target.value)}
          >
            <option value="all">All</option>
            {parentOptions.map((p) => (
              <option key={p.code} value={p.code}>{p.name}</option>
            ))}
          </select>
        </div>

        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="journal-child">Child account</label>
          <select
            id="journal-child"
            className="owner-form-select"
            value={draft.childCode}
            onChange={(e) => setDraft((d) => ({ ...d, childCode: e.target.value }))}
          >
            <option value="all">All</option>
            {childOptions.map((c) => (
              <option key={`${c.parentCode}-${c.code}`} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="journal-start">Start date</label>
          <input
            id="journal-start"
            type="date"
            className="owner-form-input"
            value={draft.startDate}
            max={draft.endDate || undefined}
            onChange={(e) => setDraft((d) => ({ ...d, startDate: e.target.value }))}
          />
        </div>

        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="journal-end">End date</label>
          <input
            id="journal-end"
            type="date"
            className="owner-form-input"
            value={draft.endDate}
            min={draft.startDate || undefined}
            onChange={(e) => setDraft((d) => ({ ...d, endDate: e.target.value }))}
          />
        </div>

        <div className="admin-finance-journal-filter-actions">
          <button type="submit" className="owner-btn owner-btn-primary owner-btn-sm">
            Search
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleClear}>
            Clear
          </button>
        </div>
      </form>

      {hasSearched && (
        <p className="owner-placeholder-text admin-finance-journal-applied">
          Showing: {appliedLabel || 'All'}
          {applied.startDate || applied.endDate ? '' : ` · as of ${localDateKey()}`}
        </p>
      )}

      {error && (
        <p className="owner-placeholder-text" style={{ padding: '0 24px 12px 20px', color: 'var(--color-danger)' }}>
          {error}
        </p>
      )}
      {loading && (
        <p className="owner-placeholder-text" style={{ padding: '0 24px 12px 20px' }}>
          Loading accounts...
        </p>
      )}

      {hasSearched ? (
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table admin-finance-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Parent</th>
                <th>Child</th>
                <th>Description</th>
                <th>Ref</th>
                <th>Type</th>
                <th>Debit</th>
                <th>Credit</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filteredEntries.length ? filteredEntries.map((row) => (
                <tr key={`${row.id}-${row.childCode}`}>
                  <td>{row.date}</td>
                  <td>{row.parentName}</td>
                  <td>{row.childName}</td>
                  <td>{row.description}</td>
                  <td>{row.ref}</td>
                  <td>{journalEntryType(row)}</td>
                  <td className="finance-amount-negative">{row.debit ? formatCurrency(row.debit) : '—'}</td>
                  <td className="finance-amount-positive">{row.credit ? formatCurrency(row.credit) : '—'}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={8} className="owner-placeholder-text">
                    {loading ? 'Loading...' : 'No ledger entries match these filters.'}
                  </td>
                </tr>
              )}
            </tbody>
            {filteredEntries.length > 0 ? (
              <tfoot>
                <tr className="admin-finance-total-row">
                  <td colSpan={6}><strong>Total</strong></td>
                  <td className="finance-amount-negative"><strong>{formatCurrency(totals.debit)}</strong></td>
                  <td className="finance-amount-positive"><strong>{formatCurrency(totals.credit)}</strong></td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      ) : (
        <p className="owner-placeholder-text" style={{ padding: '8px 24px 24px 20px' }}>
          Choose parent/child and dates, then click Search to load the ledger.
        </p>
      )}
    </div>
    </>
  );
}

export default JournalLedger;
